import React from 'react';
import { Formik, Form, Field } from 'formik';
import { titleize } from 'inflection';

import styles from './userNotificationsShowPageStyles';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import SingleCheckboxField from 'buyside/components/presentation/Form/Field/SingleCheckboxField/singleCheckboxField';
import InputField from 'buyside/components/presentation/Form/Field/InputField/inputField';

const {
  siteTypes,
  retailSiteTypes,
  userTypes,
  capIntroAllocatorTypes,
  capIntroFundAdminTypes,
  activityTypes,
  ideaSearchAlertFrequencies
} = require('buyside/util/constantsFromRails.json');

const EmailSubScriptionForm = ({ handleUpdateEmailPreferences, currentUser, initialValues }) => {
  const {
    canMessage,
    canReceiveViewerIntroRequest,
    canWriteIdeaComments,
    canWriteWhitePapers,
    fundProfileSearchAlerts,
    hasIdeas,
    ideaSearchAlerts,
    isAllocator,
    isFundProfileAdmin,
    isRetail
  } = currentUser;

  const siteNotifications = isRetail ? retailSiteTypes : siteTypes;

  const activityNotifications = Object.keys(activityTypes)
    .map(key => {
      if (['commentCreateFollowedIdea', 'ideaUpdateCreateFollowedIdea'].includes(key))
        return canWriteIdeaComments ? key : null;
      else if (key === 'positionCreate') return isRetail ? null : key;
      else return key;
    })
    .filter(Boolean);

  const userNotifications = Object.keys(userTypes)
    .map(key => {
      if (key === 'userToUser') return canMessage ? key : null;
      else if (['commentCreateAuthorFollowedIdea', 'rateIdea', 'updateIdea'].includes(key))
        return hasIdeas ? key : null;
      else if (key === 'viewerIntroRequest') return canReceiveViewerIntroRequest ? key : null;
      else if (key === 'commentCreateWhitePaperAuthor') return canWriteWhitePapers ? key : null;
      else if (key === 'commentCreatePostAuthor') return isRetail ? null : key;
      else return key;
    })
    .filter(Boolean);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        handleUpdateEmailPreferences(values, resetForm);
      }}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <div className={styles.categoryContainer}>
            <div className={styles.categoryHeader}>
              <header className={styles.categoryTitle}>Site Announcements</header>
            </div>
            <div className={styles.categoryContentContainer}>
              {Object.keys(siteNotifications).map(key => (
                <div key={key}>
                  <Field
                    component={SingleCheckboxField}
                    disabled={values.all}
                    label={siteNotifications[key]}
                    name={key}
                    overrideStyles={styles}
                    type="checkbox"
                    value={values[key]}
                  />
                </div>
              ))}
              {!currentUser.allocator && !currentUser.isRetail && (
                <div key="weeklyDigestEmail">
                  <Field
                    component={SingleCheckboxField}
                    disabled={values.all}
                    label="Weekly Buyside Update"
                    name="weeklyDigestEmail"
                    overrideStyles={styles}
                    type="checkbox"
                    value={values.weeklyDigestEmail}
                  />
                </div>
              )}
              <div key="activityDigestMailer">
                <Field
                  component={SingleCheckboxField}
                  disabled={values.all}
                  label="Watchlist Activity"
                  name="activityDigestMailer"
                  overrideStyles={styles}
                  type="checkbox"
                  value={values.activityDigestMailer}
                />
              </div>
            </div>
          </div>
          {userNotifications.length ? (
            <div className={styles.categoryContainer}>
              <div className={styles.categoryHeader}>
                <header className={styles.categoryTitle}>Messaging</header>
              </div>
              <div className={styles.categoryContentContainer}>
                {userNotifications.map(key => {
                  return (
                    <div key={key}>
                      <Field
                        component={SingleCheckboxField}
                        disabled={values.all}
                        label={userTypes[key]}
                        name={key}
                        overrideStyles={styles}
                        type="checkbox"
                        value={values[key]}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {isAllocator && (
            <div className={styles.categoryContainer}>
              <div className={styles.categoryHeader}>
                <header className={styles.categoryTitle}>Allocator</header>
              </div>
              <div className={styles.categoryContentContainer}>
                {Object.keys(capIntroAllocatorTypes).map(key => (
                  <div key={key}>
                    <Field
                      component={SingleCheckboxField}
                      disabled={values.all}
                      label={capIntroAllocatorTypes[key]}
                      name={key}
                      overrideStyles={styles}
                      type="checkbox"
                      value={values[key]}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {isFundProfileAdmin && (
            <div className={styles.categoryContainer}>
              <div className={styles.categoryHeader}>
                <header className={styles.categoryTitle}>Fund</header>
              </div>
              <div className={styles.categoryContentContainer}>
                {Object.keys(capIntroFundAdminTypes).map(key => (
                  <div key={key}>
                    <Field
                      component={SingleCheckboxField}
                      disabled={values.all}
                      label={capIntroFundAdminTypes[key]}
                      name={key}
                      overrideStyles={styles}
                      type="checkbox"
                      value={values[key]}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={styles.categoryContainer}>
            <div className={styles.categoryHeader}>
              <header className={styles.categoryTitle}>Site Activity</header>
            </div>
            <div className={styles.categoryContentContainer}>
              {activityNotifications.map(key => (
                <div key={key}>
                  <Field
                    component={SingleCheckboxField}
                    disabled={values.all}
                    label={activityTypes[key]}
                    name={key}
                    overrideStyles={styles}
                    type="checkbox"
                    value={values[key]}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.categoryContainer}>
            <div className={styles.categoryHeader}>
              <header className={styles.categoryTitle}>Your Saved Search Alerts</header>
            </div>
            <div className={styles.categoryContentContainer}>
              {!!ideaSearchAlerts.length || !!fundProfileSearchAlerts.length ? (
                <div>
                  {!!ideaSearchAlerts.length && (
                    <div className={styles.alertsContainer}>
                      <div className={styles.ideaAlerts}>
                        <h5>Idea Alerts</h5>
                        <span>Email frequency:</span>
                        <Field
                          component={InputField}
                          customStyles={styles}
                          disabled={values.all}
                          name="ideaAlertsFrequency"
                          options={ideaSearchAlertFrequencies}
                          required
                          selectOptionsFormat={option => ({
                            label: titleize(option),
                            value: option
                          })}
                          type="select"
                          value={values.ideaAlertsFrequency}
                        />
                      </div>
                      {ideaSearchAlerts.map(({ id, name }) => (
                        <div data-testid="idea-alerts" key={id}>
                          <Field
                            component={SingleCheckboxField}
                            disabled={values.all}
                            label={name}
                            name={`ideaSearchAlerts[${id}]`}
                            overrideStyles={styles}
                            type="checkbox"
                            value={values.ideaSearchAlerts[id]}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {!!fundProfileSearchAlerts.length && (
                    <div className={styles.alertsContainer}>
                      <h5>Fund Alerts</h5>
                      {fundProfileSearchAlerts.map(({ id, name }) => (
                        <div data-testid="fund-alerts" key={id}>
                          <Field
                            component={SingleCheckboxField}
                            disabled={values.all}
                            label={name}
                            name={`fundSearchAlerts[${id}]`}
                            overrideStyles={styles}
                            type="checkbox"
                            value={values.fundSearchAlerts[id]}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.noSavedSearchMsg}>You currently have no saved searches</div>
              )}
            </div>
          </div>

          <div className={styles.categoryContainer}>
            <div className={styles.categoryHeader}>
              <header className={styles.categoryTitle}>Unsubscribe from all</header>
            </div>
            <div className={styles.categoryContentContainer}>
              <Field
                component={SingleCheckboxField}
                label="Unsubscribe from all notifications"
                name="all"
                type="checkbox"
                overrideStyles={styles}
                value={values.all}
              />
            </div>
          </div>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            text="Update Email Preferences"
            isLoading={isSubmitting}
          />
        </Form>
      )}
    </Formik>
  );
};

export default EmailSubScriptionForm;
